import { AGREEMENT_JURISDICTION, AGREEMENT_SOP_CITATION } from './agreementText';
import type { InspectionService } from './inspectionService';
import { signatureIsMeaningful, type SignatureStrokes } from './signature';

// Inspection Agreement (Owner task book 2026-09-04 Round 2 §C): the previously approved
// pre-inspection agreement is a FIXED, reusable document (src/domain/agreementText.ts — the
// authorized scope of services in English and Chinese, the New York Standards of Practice opening
// sentence and the two New York mandatory clauses, all verbatim). Per inspection only the variable
// fields change: property · client · inspector · inspection date-time (all on the inspection record),
// the manually recorded inspection fee OR the Demo state, and the two hand-drawn signatures.
// There is no scope selector, no terms toggle and no payment flow: the client pays the inspector
// outside the app and the inspector records the amount.
//
// NY F1 Round 5 (OD-8 / OD-9, Owner rulings 2026-09-09) adds three facts to the record:
//   - the Client's VOLUNTARY consent to electronic processing and AI-assisted analysis (agreement
//     v2 §1). Signing works without it; it is what opens cloud AI for a real client (PP-21 as
//     amended by OD-8), and it can be withdrawn in writing without re-opening the agreement;
//   - the approved TEXT VERSION the signatures were given under, so an agreement signed under the
//     2026-08-25 copy still prints the text that was signed;
//   - the SERVICE it was signed for — the ordinary home inspection or the New Build variant (§4).
export interface V2AgreementParty {
  // Typed signer name (defaults from the inspection's client / inspector when signing).
  name: string;
  // Hand-drawn mark (vector strokes) — the act of signing.
  signature: SignatureStrokes | null;
  // "I have read and agree" confirmation given while signing.
  accepted: boolean;
  signedAt: number | null;
}

export interface V2AgreementFee {
  // Manually recorded amount in USD as typed ("450", "450.00"); '' = not recorded.
  amount: string;
}

// v4 §6 (Owner 2026-10-19, decision list item 12): an additional service agreed for this property, with
// its cost (19 NYCRR 197-4.2(a) "the cost associated with that service"). Amount as typed, like the fee.
export interface V2AdditionalService {
  name: string;
  fee: string;
}

// The Client signed on their own device through a one-time link (Owner decision list 2026-10-19 items 4 / 45):
// when the server received it and from where — the electronic signature's evidence, printed under the Client's
// signature on the agreement PDF.
export interface V2RemoteSignature {
  receivedAt: number;
  ip: string | null;
  userAgent: string | null;
}

// The copy of 2026-10-28 (v5): the agreement is printed in ONE language, the one chosen for this client.
export type AgreementLanguage = 'zh-CN' | 'en-US';

export interface V2Agreement {
  // Which approved document wording this agreement was DRAWN under (provenance; never rewritten).
  documentVersion: string;
  jurisdiction: typeof AGREEMENT_JURISDICTION;
  // v5 (Owner 2026-10-28: 「按照客户的语言选择单独出，不用双语」): the language the document is printed and signed
  // in. Part of what is signed, so changing it re-opens the document. A copy older than v5 is bilingual and
  // ignores it.
  documentLanguage: AgreementLanguage;
  fee: V2AgreementFee;
  // Demo / 演示验屋: no fee, every generated output is watermarked DEMO.
  demo: boolean;
  client: V2AgreementParty;
  inspector: V2AgreementParty;
  // NY F1 Round 5 (OD-8): the Client ticked the consent clause of agreement v2. Voluntary — a client
  // who does not consent gets exactly the same inspection and report. false on every record written
  // before the field existed.
  clientAiConsent: boolean;
  // v4 (Owner 2026-10-19, Q3): the Client ticks the consent in the signing sheet; this is when. null on
  // records written before the copy of 2026-10-19 (the inspector flipped a switch then).
  clientAiConsentAt: number | null;
  // When the Client withdrew that consent in writing (v2 §1 last bullet). The signed document is NOT
  // re-opened by a withdrawal: the signatures stand, the AI path closes.
  aiConsentWithdrawnAt: number | null;
  // v4 §3 (Owner 2026-10-19, Q2): the Client's consent to electronic records and signatures
  // (15 U.S.C. §7001(c)). From the copy of 2026-10-19 it is REQUIRED before signing electronically.
  clientEsignConsent: boolean;
  clientEsignConsentAt: number | null;
  // v5 (Owner 2026-10-28: 「其余那你建议改」 — the limitation of liability and claims terms): the Client's own tick
  // that they read and agree to those terms. REQUIRED from the copy of 2026-10-28 before signing.
  clientLiabilityAck: boolean;
  clientLiabilityAckAt: number | null;
  // v4 §6: additional services with their cost; [] prints "None.".
  additionalServices: V2AdditionalService[];
  // The approved text version in force when BOTH parties signed; null while unsigned.
  signedVersion: string | null;
  // The service the signatures were given for (OD-9); null while unsigned.
  signedService: InspectionService | null;
  // Set when both parties have signed (the moment the signing sheet was completed).
  signedAt: number | null;
  // Present only when the Client's part came through a remote-signing link.
  clientRemote?: V2RemoteSignature | null;
}

// Document version = jurisdiction + Standards of Practice citation + the approved copy date.
const VERSION_STEM = `${AGREEMENT_JURISDICTION.toLowerCase()}-${AGREEMENT_SOP_CITATION.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`;

// The first approved production copy (archived 58401a6). Agreements signed under it stay printable.
export const AGREEMENT_V1_DOCUMENT_VERSION = `${VERSION_STEM}-copy-2026-08-25`;
// The Owner-approved copy of 2026-09-09 (consent · records and retention · scope corrections · the
// New Build variant). Docs: NY-AGREEMENT-V2-AND-REPORT-FLOW-APPROVED-DRAFT-20260909.md.
export const AGREEMENT_V2_DOCUMENT_VERSION = `${VERSION_STEM}-copy-2026-09-09`;
// The copy of 2026-09-12 (Owner, TestFlight device test): the 2026-09-09 copy, printed bilingual, plus the
// language-precedence clause (English governs) and a Chinese reading aid under each mandatory clause.
// Docs: NY-AGREEMENT-V3-LANGUAGE-ADDENDUM-20260912.md.
export const AGREEMENT_V3_DOCUMENT_VERSION = `${VERSION_STEM}-copy-2026-09-12`;
// The copy of 2026-10-19 (Owner decision list 2026-10-19): truthful records and retention, consent to
// cloud storage + AI-assisted analysis ticked by the Client, consent to electronic records and
// signatures, how the Client receives copies, additional services with their cost.
// Docs: NY-AGREEMENT-V4-ADDENDUM-20261019.md.
export const AGREEMENT_V4_DOCUMENT_VERSION = `${VERSION_STEM}-copy-2026-10-19`;
// The copy of 2026-10-28 (Owner: 「协议为什么要9页……参照一下同类产品的协议，缩减一些」 and 「按照客户的语言选择单独
// 出，不用双语」): ONE language per document, the scope by reference to the Standards of Practice, the consents
// short. The facts it states are v4's. Docs: NY-AGREEMENT-V5-SINGLE-LANGUAGE-20261028.md.
export const AGREEMENT_V5_DOCUMENT_VERSION = `${VERSION_STEM}-copy-2026-10-28`;

// The version a signature given TODAY is given under.
export const AGREEMENT_DOCUMENT_VERSION = AGREEMENT_V5_DOCUMENT_VERSION;

// Oldest → newest. An unlisted version is NOT ordered (fail-closed: it never satisfies a minimum).
export const AGREEMENT_DOCUMENT_VERSION_ORDER: readonly string[] = Object.freeze([AGREEMENT_V1_DOCUMENT_VERSION, AGREEMENT_V2_DOCUMENT_VERSION, AGREEMENT_V3_DOCUMENT_VERSION, AGREEMENT_V4_DOCUMENT_VERSION, AGREEMENT_V5_DOCUMENT_VERSION]);

// What the document and the report PRINT for a version (v4 §7): "v4 (2026-10-19)", never the internal id.
// An unknown version prints as it is stored, so nothing is ever misnamed.
export function agreementVersionLabel(version: string): string {
  const index = AGREEMENT_DOCUMENT_VERSION_ORDER.indexOf(version);
  if (index < 0) return version;
  const date = /copy-(\d{4}-\d{2}-\d{2})$/.exec(version)?.[1];
  return date ? `v${index + 1} (${date})` : `v${index + 1}`;
}

// PP-21 as amended by OD-8: cloud AI needs an agreement signed under a copy that CARRIES the consent
// clause. The 2026-08-25 copy has none, so an inspection signed under it stays blocked.
export const AI_CONSENT_MIN_DOCUMENT_VERSION = AGREEMENT_V2_DOCUMENT_VERSION;

export function agreementVersionAtLeast(version: string | null, minimum: string): boolean {
  if (version === null) return false;
  const index = AGREEMENT_DOCUMENT_VERSION_ORDER.indexOf(version);
  const floor = AGREEMENT_DOCUMENT_VERSION_ORDER.indexOf(minimum);
  return index >= 0 && floor >= 0 && index >= floor;
}

export function emptyParty(): V2AgreementParty {
  return { name: '', signature: null, accepted: false, signedAt: null };
}

// v5: English unless the client asks for Chinese (Owner 2026-10-28: 「可以要求中文就是中文，老外客户直接出英文」).
export function defaultAgreement(documentLanguage: AgreementLanguage = 'en-US'): V2Agreement {
  return {
    documentVersion: AGREEMENT_DOCUMENT_VERSION,
    jurisdiction: AGREEMENT_JURISDICTION,
    documentLanguage,
    fee: { amount: '' },
    demo: false,
    client: emptyParty(),
    inspector: emptyParty(),
    clientAiConsent: false,
    clientAiConsentAt: null,
    aiConsentWithdrawnAt: null,
    clientEsignConsent: false,
    clientEsignConsentAt: null,
    clientLiabilityAck: false,
    clientLiabilityAckAt: null,
    additionalServices: [],
    signedVersion: null,
    signedService: null,
    signedAt: null
  };
}

// The copy a requirement is judged against: the one it was signed under, else the copy being signed now.
// (Not agreementTextVersion — that one asks whether the agreement is signed, which asks this.)
function requirementVersion(agreement: V2Agreement): string {
  return agreement.signedVersion ?? AGREEMENT_DOCUMENT_VERSION;
}

export function additionalServiceComplete(service: V2AdditionalService): boolean {
  return service.name.trim().length > 0 && service.fee.trim().length > 0;
}

export function partySigned(party: V2AgreementParty): boolean {
  return party.name.trim().length > 0 && party.accepted && signatureIsMeaningful(party.signature);
}

// What the signing step is still waiting on, in document order. Empty = the agreement is signed.
// The AI consent is deliberately NOT here: it is voluntary (v2 §1) and never blocks signing.
// From the copy of 2026-10-19 the consent to electronic records and signatures IS here: without it the
// Client cannot sign on the device (the paper route happens outside the app). An agreement signed under
// an older copy is judged by that copy, so it never falls back to unsigned.
// From the copy of 2026-10-28 the Client's tick on the limitation of liability and claims terms is required too.
export type AgreementRequirement = 'fee' | 'additionalServices' | 'clientName' | 'clientSignature' | 'clientAccepted' | 'clientEsignConsent' | 'clientLiabilityAck' | 'inspectorName' | 'inspectorSignature' | 'inspectorAccepted';

export function missingAgreementRequirements(agreement: V2Agreement): AgreementRequirement[] {
  const missing: AgreementRequirement[] = [];
  const v4 = agreementVersionAtLeast(requirementVersion(agreement), AGREEMENT_V4_DOCUMENT_VERSION);
  const v5 = agreementVersionAtLeast(requirementVersion(agreement), AGREEMENT_V5_DOCUMENT_VERSION);
  // A Demo inspection has no fee by design; otherwise the fee is recorded before signing.
  if (!agreement.demo && !feeRecorded(agreement.fee)) missing.push('fee');
  if (v4 && agreement.additionalServices.some((service) => !additionalServiceComplete(service))) missing.push('additionalServices');
  if (agreement.client.name.trim().length === 0) missing.push('clientName');
  if (!signatureIsMeaningful(agreement.client.signature)) missing.push('clientSignature');
  if (!agreement.client.accepted) missing.push('clientAccepted');
  if (v4 && !agreement.clientEsignConsent) missing.push('clientEsignConsent');
  if (v5 && !agreement.clientLiabilityAck) missing.push('clientLiabilityAck');
  if (agreement.inspector.name.trim().length === 0) missing.push('inspectorName');
  if (!signatureIsMeaningful(agreement.inspector.signature)) missing.push('inspectorSignature');
  if (!agreement.inspector.accepted) missing.push('inspectorAccepted');
  return missing;
}

// Signed = both parties signed. `signedAt` alone proves nothing: stored is not signed.
export function agreementSigned(agreement: V2Agreement): boolean {
  return agreement.signedAt !== null && missingAgreementRequirements(agreement).length === 0;
}

// The service the signatures were given for. A record signed before the New Build variant existed
// could only be an ordinary home inspection, so a missing value reads as that.
export function agreementSignedService(agreement: V2Agreement): InspectionService | null {
  return agreementSigned(agreement) ? (agreement.signedService ?? 'homeInspection') : null;
}

// X-1 (Owner default 2026-09-11) / OD-9: an agreement counts for THIS engagement only when it was
// signed for the service the property calls for. Answering "newly built" after signing therefore
// asks for a signature on the New Build document, and never carries the old one over.
export function agreementSignedFor(agreement: V2Agreement, service: InspectionService): boolean {
  return agreementSignedService(agreement) === service;
}

// Which approved text this agreement carries: the version it was signed under while signed, the
// current copy while it is still open (that is the text the signing sheet presents).
export function agreementTextVersion(agreement: V2Agreement): string {
  if (!agreementSigned(agreement)) return AGREEMENT_DOCUMENT_VERSION;
  return agreement.signedVersion ?? agreement.documentVersion;
}

// PP-21 / OD-8, as ONE derivation over the record.
//   unsigned         no signed agreement for this engagement yet
//   versionOutdated  signed under a copy that carries no consent clause
//   notConsented     signed under the current copy, consent not given or withdrawn
//   consented        the client's prior, express consent stands
export type AgreementAiConsent = 'unsigned' | 'versionOutdated' | 'notConsented' | 'consented';

export function agreementAiConsentOf(agreement: V2Agreement): AgreementAiConsent {
  if (!agreementSigned(agreement)) return 'unsigned';
  if (!agreementVersionAtLeast(agreementTextVersion(agreement), AI_CONSENT_MIN_DOCUMENT_VERSION)) return 'versionOutdated';
  if (!agreement.clientAiConsent || agreement.aiConsentWithdrawnAt !== null) return 'notConsented';
  return 'consented';
}

// v4 consent clause (2) and RECORDS AND RETENTION: the final report PDF, any addendum and the signed agreement are
// stored in the Inspector's account "if the Client consented above". A copy older than v4 never mentioned that
// storage, so it never allows it; a withdrawn consent no longer does. A Demo has no client: its PDFs are the
// inspector's own practice record and may be kept in the account.
// Read from the facts that survive the final report's clean-up (OD-12 keeps the consent, its withdrawal, the
// signing time and the signed copy, not the signatures), so an addendum made later is judged the same way.
export function agreementAllowsCloudStorage(agreement: V2Agreement): boolean {
  if (agreement.demo) return true;
  return agreement.signedAt !== null && agreement.clientAiConsent && agreement.aiConsentWithdrawnAt === null && agreementVersionAtLeast(agreement.signedVersion, AGREEMENT_V4_DOCUMENT_VERSION);
}

export function feeRecorded(fee: Pick<V2AgreementFee, 'amount'>): boolean {
  return fee.amount.trim().length > 0;
}

// The fee is a manual amount: digits and one decimal point survive ("$1,250.00" → "1250.00").
// Changing it after signing re-opens the document (the signatures were given for the old amount).
export function withAgreementFee(agreement: V2Agreement, amount: string): V2Agreement {
  const next = amount.replace(/[^0-9.]/g, '').trim();
  if (next === agreement.fee.amount) return agreement;
  return unsigned({ ...agreement, fee: { amount: next } });
}

// Demo clears the fee (a demo is never billed). Switching Demo on or off changes what was signed
// for, so it re-opens the document too.
export function withAgreementDemo(agreement: V2Agreement, demo: boolean): V2Agreement {
  if (agreement.demo === demo) return agreement;
  return unsigned({ ...agreement, demo, fee: demo ? { amount: '' } : agreement.fee });
}

// v5: a document of this copy is printed in its documentLanguage alone; an older copy is bilingual.
export function agreementIsSingleLanguage(version: string): boolean {
  return agreementVersionAtLeast(version, AGREEMENT_V5_DOCUMENT_VERSION);
}

// v5: the language chosen for this client. The words the parties sign change with it, so — like the fee — it
// re-opens the document, the Client's part from a signing link included.
export function withAgreementLanguage(agreement: V2Agreement, documentLanguage: AgreementLanguage): V2Agreement {
  if (agreement.documentLanguage === documentLanguage) return agreement;
  return unsigned({ ...agreement, documentLanguage });
}

// The client's consent tick (v2 §1). It is part of the document that gets signed, so changing it on
// an already signed agreement re-opens it: the client ticks the box and signs again. From v4 the Client
// ticks it in the signing sheet and the time of the tick is kept (null when unticked).
export function withClientAiConsent(agreement: V2Agreement, consent: boolean, now: number | null = null): V2Agreement {
  if (agreement.clientAiConsent === consent) return agreement;
  const next = { ...agreement, clientAiConsent: consent, clientAiConsentAt: consent ? now : null };
  return agreementSigned(agreement) ? unsigned(next) : next;
}

// v4 §3: the Client's consent to electronic records and signatures. Like the AI consent it is part of the
// signed document, so changing it on a signed agreement re-opens it.
export function withClientEsignConsent(agreement: V2Agreement, consent: boolean, now: number | null = null): V2Agreement {
  if (agreement.clientEsignConsent === consent) return agreement;
  const next = { ...agreement, clientEsignConsent: consent, clientEsignConsentAt: consent ? now : null };
  return agreementSigned(agreement) ? unsigned(next) : next;
}

// v5: the Client's tick on the limitation of liability and claims terms. Part of the signed document, so changing
// it on a signed agreement re-opens it.
export function withClientLiabilityAck(agreement: V2Agreement, ack: boolean, now: number | null = null): V2Agreement {
  if (agreement.clientLiabilityAck === ack) return agreement;
  const next = { ...agreement, clientLiabilityAck: ack, clientLiabilityAckAt: ack ? now : null };
  return agreementSigned(agreement) ? unsigned(next) : next;
}

// v4 §6: the additional services agreed for this property. What the signatures cover, so a change
// re-opens a signed agreement. Amounts keep digits and one decimal point, like the fee.
export function withAdditionalServices(agreement: V2Agreement, services: readonly V2AdditionalService[]): V2Agreement {
  const next = services.map((service) => ({ name: service.name, fee: service.fee.replace(/[^0-9.]/g, '').trim() }));
  const same = next.length === agreement.additionalServices.length && next.every((service, index) => service.name === agreement.additionalServices[index].name && service.fee === agreement.additionalServices[index].fee);
  if (same) return agreement;
  const changed = { ...agreement, additionalServices: next };
  return agreementSigned(agreement) ? unsigned(changed) : changed;
}

// A written withdrawal before the report is generated (v2 §1 last bullet). The signed agreement is
// untouched — only the AI path closes — so the inspection never falls back into an unsigned state.
export function withAiConsentWithdrawn(agreement: V2Agreement, now: number): V2Agreement {
  if (!agreementSigned(agreement) || !agreement.clientAiConsent || agreement.aiConsentWithdrawnAt !== null) return agreement;
  return { ...agreement, aiConsentWithdrawnAt: now };
}

// Records WHICH document the signatures were given for (OD-9). The store stamps it from the
// property when a signed agreement is saved; an open agreement carries no service.
export function withSignedService(agreement: V2Agreement, service: InspectionService): V2Agreement {
  const next = agreementSigned(agreement) ? service : null;
  if (agreement.signedService === next) return agreement;
  return { ...agreement, signedService: next };
}

// The completed signing step: both marks, both names, both confirmations, one timestamp — and the
// approved text version in force at that moment.
export function withSignatures(agreement: V2Agreement, client: V2AgreementParty, inspector: V2AgreementParty, now: number): V2Agreement {
  const next: V2Agreement = {
    ...agreement,
    client: { ...client, name: client.name.trim(), signedAt: partySigned(client) ? (client.signedAt ?? now) : null },
    inspector: { ...inspector, name: inspector.name.trim(), signedAt: partySigned(inspector) ? (inspector.signedAt ?? now) : null }
  };
  const complete = missingAgreementRequirements(next).length === 0;
  next.signedAt = complete ? now : null;
  next.signedVersion = complete ? AGREEMENT_DOCUMENT_VERSION : null;
  // A fresh signature supersedes an earlier withdrawal: what the client ticks now is what stands.
  next.aiConsentWithdrawnAt = complete ? null : agreement.aiConsentWithdrawnAt;
  return next;
}

// The Client's part as it came back from a remote-signing link: name, mark, "read and agree", both ticks with
// the times the Client gave them, and the server's receipt as evidence. The Inspector still signs on their own
// device; until then the agreement stays unsigned.
export function withRemoteClientSignature(
  agreement: V2Agreement,
  part: { name: string; signature: SignatureStrokes; esignConsentAt: number; aiConsent: boolean; aiConsentAt: number | null; liabilityAckAt?: number | null; receivedAt: number; ip: string | null; userAgent: string | null }
): V2Agreement {
  // A signing page served before the liability tick existed sends none: the Client has not given it, and the
  // agreement stays open until they sign in person.
  const liabilityAckAt = typeof part.liabilityAckAt === 'number' ? part.liabilityAckAt : null;
  return {
    ...agreement,
    client: { name: part.name.trim(), signature: part.signature, accepted: true, signedAt: part.receivedAt },
    clientEsignConsent: true,
    clientEsignConsentAt: part.esignConsentAt,
    clientLiabilityAck: liabilityAckAt !== null,
    clientLiabilityAckAt: liabilityAckAt,
    clientAiConsent: part.aiConsent,
    clientAiConsentAt: part.aiConsent ? part.aiConsentAt : null,
    clientRemote: { receivedAt: part.receivedAt, ip: part.ip, userAgent: part.userAgent },
    signedAt: null,
    signedVersion: null,
    signedService: null
  };
}

// Back to signing in person: the Client's remote part is dropped (name kept), the Inspector's untouched.
export function withoutRemoteClientSignature(agreement: V2Agreement): V2Agreement {
  if (!agreement.clientRemote) return agreement;
  const { clientRemote: _remote, ...rest } = agreement;
  return { ...rest, client: { ...agreement.client, signature: null, accepted: false, signedAt: null }, clientEsignConsent: false, clientEsignConsentAt: null, clientLiabilityAck: false, clientLiabilityAckAt: null, signedAt: null, signedVersion: null, signedService: null };
}

// Any change to a variable field after signing re-opens the document (a signed agreement does
// not re-sign itself). Fee, Demo and the consent tick are the variable fields on the agreement
// itself; the consent tick survives the re-opening as the value the next signature will carry.
export function unsigned(agreement: V2Agreement): V2Agreement {
  // The Client's remote signature goes with the Client's signature.
  const { clientRemote: _remote, ...rest } = agreement;
  return {
    ...rest,
    client: { ...agreement.client, signature: null, accepted: false, signedAt: null },
    inspector: { ...agreement.inspector, signature: null, accepted: false, signedAt: null },
    aiConsentWithdrawnAt: null,
    signedVersion: null,
    signedService: null,
    signedAt: null
  };
}
